import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '../../app/Context';
import { DEFAULT_AVATAR_ID } from '../../constants';
import { fetchAvatars } from '../../db/avatars';
import { getNameInitials } from '../../utils/name';

export interface VMProps {
  avatarId: MongoId | typeof DEFAULT_AVATAR_ID;
  bucketUrl?: BucketURL;
  /** If there is an error loading the avatar image, first letter of the name would be displayed */
  name?: string;
}

export default function useUserAvatarVM({ avatarId, bucketUrl, name }: VMProps) {
  const [loaded, setLoaded] = useState(false);
  const { session, avatarsById, setAvatarsById } = useAppContext();

  const avatarURL = avatarsById[avatarId];

  const nameInitials = useMemo(() => {
    if (avatarId) return;
    if (!name) return;
    return getNameInitials(name).charAt(0);
  }, [avatarId, name]);

  const handleLoad = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    if (!session) return;
    if (!avatarId || avatarId === DEFAULT_AVATAR_ID) return;
    // avatar not found in state so fetch from server
    if (!avatarURL) {
      fetchAvatars({ bucket: session.s3BucketURL, avatarId, baseURL: session.cluster }).then((val) => {
        setAvatarsById((prevVal) => ({ ...prevVal, ...val }));
      });
    }
  }, [avatarURL, avatarId, bucketUrl, session?.s3BucketURL, session?.cluster]);

  return {
    loaded,
    nameInitials,
    handleLoad,
    avatarURL,
  };
}
